import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Building, Activity, FileText, Leaf, CheckCircle, Users, Target, Eye } from 'lucide-react';
import { companyInfo, services } from '../data/mock';

const About = () => {
  const iconMap = {
    building: Building,
    activity: Activity,
    fileText: FileText,
    leaf: Leaf
  };

  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description: "To empower healthcare institutions with world-class planning, compliance, and infrastructure solutions that enhance patient care and operational excellence."
    },
    {
      icon: Eye,
      title: "Our Vision",
      description: "To be India's most trusted healthcare consultancy partner, setting new standards in healthcare infrastructure planning and regulatory compliance."
    },
    {
      icon: CheckCircle,
      title: "Our Commitment",
      description: "Delivering exceptional quality, transparency, and timely service to every healthcare project we undertake."
    }
  ];

  const team = [
    {
      name: "Komal Singh",
      role: "Founder & CEO",
      image: "https://customer-assets.emergentagent.com/job_clinical-consultancy/artifacts/du5ed1v0_komal%20singh.jpeg",
      description: "11+ years in healthcare planning"
    },
    {
      name: "Ajay Kumar Dubey",
      role: "MBBS, MD(Radiologist)",
      image: "https://customer-assets.emergentagent.com/job_clinical-consultancy/artifacts/qzugg4wg_ajay%20kumar%20dubey.jpeg",
      description: "Co-founder & Compliance Expert | Expert in NABH & regulatory affairs"
    },
    {
      name: "Dr. Chandan Kumar",
      role: "MBBS, MS Ortho",
      image: "https://customer-assets.emergentagent.com/job_clinical-consultancy/artifacts/exuhthfv_WhatsApp%20Image%202026-06-12%20at%209.26.52%20AM.jpeg",
      description: "Associate Director – Hospital Planning & Quality Systems"
    },
    {
      name: "Ar.V.Stuti Mishra",
      role: "Principle Architect",
      image: "https://customer-assets.emergentagent.com/job_clinical-consultancy/artifacts/tsh9vqmc_architect.jpeg",
      description: "Healthcare facility design and planning expert"
    },
    {
      name: "Veer Bahadur Singh",
      role: "Co-founder & Head of Medical Equipment Services",
      image: "https://customer-assets.emergentagent.com/job_clinical-consultancy/artifacts/xh2tosgb_veer-co-founder.png",
      description: "Medical equipment specialist with extensive experience"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About {companyInfo.shortName}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {companyInfo.description}
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>
              <p className="text-gray-600 leading-relaxed">
              Biomedical Sentre Healthcare Solutions has been at the forefront of healthcare consultancy in India. What started as a small team of passionate healthcare professionals has grown into a leading consultancy firm serving over 50 healthcare institutions across the country.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Our expertise spans across healthcare architectural planning, equipment procurement, regulatory compliance, and environmental management. We take pride in our comprehensive approach that ensures every aspect of your healthcare facility meets the highest standards of quality and compliance.
              </p>
              <p className="text-gray-600 leading-relaxed">
                With a team of experienced consultants, architects, and compliance specialists, we've successfully delivered over 100 projects, helping hospitals, clinics, and diagnostic centers achieve operational excellence and regulatory compliance.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/5452189/pexels-photo-5452189.jpeg"
                alt="Healthcare Team"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-8 rounded-xl shadow-xl">
                <div className="text-5xl font-bold mb-2">15+</div>
                <div className="text-lg">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Guided by principles that drive excellence in healthcare consultancy
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-8 text-center space-y-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experienced professionals dedicated to your success
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="h-80 overflow-hidden bg-gray-100">
                  <img
                    src={member.image}
                    alt={member.name}
                    className={`w-full h-full hover:scale-110 transition-transform duration-300 ${
                      member.name === "Dr. Chandan Kumar"
                        ? "object-contain"
                        : member.name === "Ajay Kumar Dubey" || member.name === "Komal Singh" || member.name === "Ar.V.Stuti Mishra" || member.name === "Veer Bahadur Singh"
                        ? "object-cover object-top" 
                        : "object-cover"
                    }`}
                  />
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-gray-600">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Achievements
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">100+</div>
              <div className="text-xl text-blue-100">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">50+</div>
              <div className="text-xl text-blue-100">Healthcare Clients</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">95%</div>
              <div className="text-xl text-blue-100">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">15+</div>
              <div className="text-xl text-blue-100">Years Experience</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
